import { useState } from 'react';
import { AsyncBoundary, ConfirmDialog, Drawer, Notice, useToast } from '@kollez/design-system';
import {
  useHolidays, useCreateHoliday, useDeleteHoliday,
  type HolidayKind, type HolidayRow,
} from '@/lib/queries';
import { useAuthStore } from '@/lib/authStore';

/**
 * /tpo/holidays (Page 77/N) — college calendar holidays.
 *
 * Holidays drive the drive-scheduler (no drives on festivals) and
 * exam-week freezes. Was in the audit backlog as "Warn #1: holidays
 * CRUD" — API was live but no UI consumed it.
 */

const KIND_LABEL: Record<HolidayKind, string> = {
  academic: 'Academic',
  placement_freeze: 'Placement freeze',
  exam_week: 'Exam week',
  festival: 'Festival',
  other: 'Other',
};

function kindTone(k: HolidayKind): string {
  if (k === 'placement_freeze') return 'badge-danger';
  if (k === 'exam_week') return 'badge-warning';
  if (k === 'festival') return 'badge-info';
  return '';
}

function fmt(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

export function TpoHolidaysPage() {
  const user = useAuthStore((s) => s.user);
  const toast = useToast();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<HolidayKind | ''>('');
  const [creating, setCreating] = useState(false);
  const [deleteRow, setDeleteRow] = useState<HolidayRow | null>(null);

  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [newKind, setNewKind] = useState<HolidayKind>('festival');
  const [note, setNote] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);

  const list = useHolidays({ q, kind, limit: 200 });
  const create = useCreateHoliday();
  const del = useDeleteHoliday();
  const rows = list.data ?? [];

  const resetForm = () => {
    setName(''); setStartDate(''); setEndDate(''); setNewKind('festival');
    setNote(''); setIsRecurring(false);
  };

  const submit = () => {
    if (!user?.college || !name.trim() || !startDate || !endDate) return;
    create.mutate(
      {
        college: user.college, name: name.trim(), startDate, endDate,
        kind: newKind, note: note.trim() || undefined, isRecurring,
      },
      {
        onSuccess: () => { setCreating(false); resetForm(); toast.show({ message: 'Holiday added.' }); },
        onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
      },
    );
  };

  const confirmDelete = () => {
    if (!deleteRow) return;
    del.mutate(deleteRow._id, {
      onSuccess: () => { setDeleteRow(null); toast.show({ message: 'Removed.' }); },
      onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
    });
  };

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Calendar</p>
          <h1 className="h-page">Holidays</h1>
          <p className="subhead">
            Drives, interviews and mock sessions avoid these dates automatically.
            Placement freezes and exam weeks block scheduling; festivals show as
            informational.
          </p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-primary" onClick={() => setCreating(true)}>Add holiday</button>
        </div>
      </div>

      <div className="card">
        <div className="filterbar">
          <input className="input" placeholder="Search name" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="select" value={kind} onChange={(e) => setKind(e.target.value as HolidayKind | '')}>
            <option value="">Any kind</option>
            {(Object.entries(KIND_LABEL) as Array<[HolidayKind, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <span className="fb-count">{rows.length}</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={list.isLoading}
          error={list.error}
          empty={!list.isLoading && rows.length === 0}
          emptyTitle="No holidays yet"
          emptyHint="Add Dussehra, Sankranti, Christmas break, exam weeks — anything scheduling should avoid."
          onRetry={() => void list.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr><th>Name</th><th>Window</th><th>Kind</th><th>Note</th><th></th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id}>
                    <td className="cell-title">
                      {r.name}
                      {r.isRecurring && <span className="badge ml-2">recurring</span>}
                    </td>
                    <td className="cell-sub">
                      {fmt(r.startDate)}
                      {r.endDate !== r.startDate && ` → ${fmt(r.endDate)}`}
                    </td>
                    <td><span className={`badge ${kindTone(r.kind)}`}>{KIND_LABEL[r.kind]}</span></td>
                    <td className="cell-sub">{r.note ?? '—'}</td>
                    <td>
                      <button className="btn btn-sm btn-ghost btn-danger" onClick={() => setDeleteRow(r)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <Notice tone="info">
        <b>Placement freeze</b> blocks new drive dates entirely. <b>Exam week</b> nudges the
        auto-scheduler to avoid it. <b>Festival</b> is informational.
      </Notice>

      <Drawer open={creating} onClose={() => setCreating(false)} title="Add holiday" size="md">
        <div className="stack">
          <label className="block">
            <span className="eyebrow">Name</span>
            <input className="input mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="Dussehra" />
          </label>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="block">
              <span className="eyebrow">Starts</span>
              <input className="input mt-1" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </label>
            <label className="block">
              <span className="eyebrow">Ends</span>
              <input className="input mt-1" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </label>
          </div>
          <label className="block">
            <span className="eyebrow">Kind</span>
            <select className="select mt-1" value={newKind} onChange={(e) => setNewKind(e.target.value as HolidayKind)}>
              {(Object.entries(KIND_LABEL) as Array<[HolidayKind, string]>).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="eyebrow">Note (optional)</span>
            <textarea className="input mt-1" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={isRecurring} onChange={(e) => setIsRecurring(e.target.checked)} />
            <span>Repeats every year</span>
          </label>
          <div className="flex justify-end gap-2">
            <button className="btn btn-ghost" onClick={() => setCreating(false)}>Cancel</button>
            <button
              className="btn btn-primary"
              disabled={!name.trim() || !startDate || !endDate || create.isPending}
              onClick={submit}
            >
              {create.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </Drawer>

      <ConfirmDialog
        open={Boolean(deleteRow)}
        onClose={() => setDeleteRow(null)}
        onConfirm={confirmDelete}
        title="Remove holiday?"
        description={deleteRow ? `"${deleteRow.name}" (${fmt(deleteRow.startDate)}) will be removed from the calendar.` : ''}
        confirmLabel="Remove"
        tone="danger"
      />
    </div>
  );
}
